import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  Building2,
  Check,
  Plus,
  Search,
  ServerCog,
  Users,
} from 'lucide-react';
import {
  SUPPLIER_KIND_LABEL,
  type ProtheusSearchResponseDto,
  type ProtheusSupplierDto,
  type SupplierListResponseDto,
  type SupplierStoreListItemDto,
} from '@weloze/shared';
import { api, ApiError } from '../../lib/api';
import { useUrlFilter } from '../../lib/urlFilter';
import { formatCnpj, formatCompetence, formatPlace } from '../../lib/format';
import {
  Alert,
  Badge,
  Button,
  EmptyState,
  Field,
  Modal,
  Select,
  Skeleton,
  SortHeader,
  cn,
} from '../../components/ui';
import { useTableSort } from '../../lib/sorting';

/**
 * `/admin/fornecedores` — cadastro de monitorados (PLAN §7, tela 10).
 *
 * Monitorar é diferente de dar acesso: esta tela decide **quem o portal
 * calcula**, e não quem enxerga. Um fornecedor pode ser monitorado por um ano
 * inteiro sem nunca receber um login.
 *
 * A unidade é sempre **código + loja**. Duas lojas do mesmo código são linhas
 * distintas com nota própria — foi por não separá-las que a lista antiga de RNC
 * ficou ambígua para fornecedor com mais de uma loja.
 */

type Scope = 'monitored' | 'all' | 'unmonitored';

const SCOPE_LABEL: Record<Scope, string> = {
  monitored: 'Monitorados',
  all: 'Todos',
  unmonitored: 'Não monitorados',
};

/** Competência sugerida na inclusão: a carga inicial combinada no PLAN §1. */
const DEFAULT_COMPETENCE = '2026-01';

/**
 * Filtro da ficha.
 *
 * "Com pendência" é a pergunta que a Qualidade faz para saber o que cobrar hoje;
 * "sem pendência" existe para conferir o outro lado da conta, que é como se
 * descobre que um filtro está errado.
 */
type Ficha = 'all' | 'pending' | 'clean';

const FICHA_LABEL: Record<Ficha, string> = {
  all: 'Todas',
  pending: 'Com pendência',
  clean: 'Sem pendência',
};

export function SuppliersPage() {
  const [scope, setScope] = useState<Scope>('monitored');
  const [search, setSearch] = useState('');
  const [uf, setUf] = useState('');
  const [ficha, setFicha] = useState<Ficha>('all');
  // O contador “fichas com pendência” do painel chega por aqui.
  useUrlFilter('ficha', ['all', 'pending', 'clean'] as const, setFicha);
  const [data, setData] = useState<SupplierListResponseDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ scope });
      if (search.trim()) params.set('search', search.trim());
      setData(await api.get<SupplierListResponseDto>(`/suppliers?${params}`));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível carregar a lista.');
    } finally {
      setLoading(false);
    }
  }, [scope, search]);

  // Espera a digitação parar: sem isso é uma requisição por tecla.
  useEffect(() => {
    const timer = setTimeout(() => void load(), search ? 350 : 0);
    return () => clearTimeout(timer);
  }, [load, search]);

  const pendingTotal = useMemo(
    () => (data?.items ?? []).reduce((sum, item) => sum + item.pendencyCount, 0),
    [data],
  );

  // As UFs vêm da própria carteira, e não de uma lista dos 27 estados: um
  // seletor com 27 opções onde só 8 têm fornecedor faz procurar no vazio.
  const ufs = useMemo(
    () =>
      [...new Set((data?.items ?? []).map((item) => item.state).filter(Boolean))].sort() as string[],
    [data],
  );

  // Filtrados no navegador porque a listagem já traz a carteira inteira: mandar
  // UF e ficha para o servidor seria uma ida ao banco para não mudar nada.
  const visiveis = useMemo(() => {
    return (data?.items ?? []).filter((item) => {
      if (uf && item.state !== uf) return false;
      if (ficha === 'pending' && item.pendencyCount === 0) return false;
      if (ficha === 'clean' && item.pendencyCount > 0) return false;
      return true;
    });
  }, [data, uf, ficha]);

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Fornecedores</h1>
          <p className="mt-1 text-sm text-slate-600">
            Quem o portal calcula. Monitorar é independente de dar acesso ao portal.
          </p>
        </div>
        <Button onClick={() => setAdding(true)}>
          <Plus className="size-4" aria-hidden />
          Incluir fornecedor
        </Button>
      </header>

      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[16rem] flex-1">
          <Field
            label="Buscar"
            name="busca"
            icon={<Search className="size-4" />}
            placeholder="Nome, código, CNPJ, cidade ou UF"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Select
          label="Situação"
          name="escopo"
          className="w-44"
          value={scope}
          onChange={(e) => setScope(e.target.value as Scope)}
        >
          {(Object.keys(SCOPE_LABEL) as Scope[]).map((value) => (
            <option key={value} value={value}>
              {SCOPE_LABEL[value]}
            </option>
          ))}
        </Select>
        <Select
          label="UF"
          name="uf"
          className="w-28"
          value={uf}
          onChange={(e) => setUf(e.target.value)}
        >
          <option value="">Todas</option>
          {ufs.map((sigla) => (
            <option key={sigla} value={sigla}>
              {sigla}
            </option>
          ))}
        </Select>
        <Select
          label="Ficha"
          name="ficha"
          className="w-44"
          value={ficha}
          onChange={(e) => setFicha(e.target.value as Ficha)}
        >
          {(Object.keys(FICHA_LABEL) as Ficha[]).map((value) => (
            <option key={value} value={value}>
              {FICHA_LABEL[value]}
            </option>
          ))}
        </Select>
      </div>

      {error && <Alert>{error}</Alert>}

      {data && !loading && (
        <div className="flex flex-wrap gap-2 text-sm text-slate-600">
          <Badge tone="brand">
            {data.monitoredCount} loja{data.monitoredCount === 1 ? '' : 's'} monitorada
            {data.monitoredCount === 1 ? '' : 's'}
          </Badge>
          {pendingTotal > 0 && (
            <Badge tone="warning">
              <AlertTriangle className="size-3" aria-hidden />
              {pendingTotal} pendência{pendingTotal === 1 ? '' : 's'} de ficha
            </Badge>
          )}
          {visiveis.length !== data.items.length && (
            <span className="text-xs text-slate-500">
              mostrando {visiveis.length} de {data.items.length}
            </span>
          )}
        </div>
      )}

      {loading ? (
        <div className="space-y-2">
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-14" />
          ))}
        </div>
      ) : visiveis.length > 0 ? (
        <SupplierTable items={visiveis} />
      ) : (
        <EmptyState
          title="Nenhum fornecedor encontrado"
          description={
            search || uf || ficha !== 'all'
              ? 'Nenhuma loja casa com a busca e os filtros. Limpe um deles para ver mais.'
              : 'Inclua o primeiro fornecedor monitorado para o portal começar a calcular.'
          }
          action={
            <Button onClick={() => setAdding(true)}>
              <Plus className="size-4" aria-hidden />
              Incluir fornecedor
            </Button>
          }
        />
      )}

      <AddSupplierModal
        open={adding}
        onClose={() => setAdding(false)}
        onDone={() => {
          setAdding(false);
          void load();
        }}
      />
    </div>
  );
}

const COLUNAS = {
  nome: (item: SupplierStoreListItemDto) => item.storeName,
  codigo: (item: SupplierStoreListItemDto) => item.protheusRef,
  tipo: (item: SupplierStoreListItemDto) => SUPPLIER_KIND_LABEL[item.supplierKind],
  local: (item: SupplierStoreListItemDto) => formatPlace(item.city, item.state),
  monitorado: (item: SupplierStoreListItemDto) => item.monitoredFrom,
  ficha: (item: SupplierStoreListItemDto) => item.pendencyCount,
};

function SupplierTable({ items }: { items: SupplierStoreListItemDto[] }) {
  // Alfabético pelo nome: é como se procura numa lista, e é a ordem que o
  // servidor já devolve quando não há busca.
  const { rows, sort, toggle } = useTableSort(items, COLUNAS, {
    key: 'nome',
    direction: 'asc',
  });

  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
      <table className="w-full min-w-[54rem] text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
            <SortHeader column="nome" sort={sort} onSort={toggle}>
              Fornecedor
            </SortHeader>
            <SortHeader column="codigo" sort={sort} onSort={toggle}>
              Código/Loja
            </SortHeader>
            <SortHeader column="tipo" sort={sort} onSort={toggle}>
              Tipo
            </SortHeader>
            <SortHeader column="local" sort={sort} onSort={toggle}>
              Local
            </SortHeader>
            <SortHeader column="monitorado" sort={sort} onSort={toggle}>
              Monitorado
            </SortHeader>
            <SortHeader column="ficha" sort={sort} onSort={toggle}>
              Ficha
            </SortHeader>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((item) => (
            <tr key={item.storeId} className="transition-colors hover:bg-slate-50/70">
              <td className="px-4 py-3">
                <Link
                  to={`/admin/fornecedores/${item.storeId}`}
                  className="font-medium text-brand-700 underline-offset-4 hover:underline"
                >
                  {item.storeName}
                </Link>
                <p className="text-xs text-slate-500">{formatCnpj(item.cnpj)}</p>
              </td>
              <td className="px-4 py-3">
                <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs text-slate-700">
                  {item.protheusRef}
                </span>
              </td>
              <td className="px-4 py-3 text-slate-600">
                {SUPPLIER_KIND_LABEL[item.supplierKind]}
                {item.productCategories.length > 0 && (
                  <p className="text-xs text-slate-400">{item.productCategories.join(', ')}</p>
                )}
              </td>
              <td className="px-4 py-3 text-slate-600">{formatPlace(item.city, item.state)}</td>
              <td className="px-4 py-3">
                {item.monitored ? (
                  <Badge tone="success">
                    <Check className="size-3" aria-hidden />
                    desde {formatCompetence(item.monitoredFrom)}
                  </Badge>
                ) : (
                  <Badge>
                    {item.monitoredUntil
                      ? `até ${formatCompetence(item.monitoredUntil)}`
                      : 'não monitorado'}
                  </Badge>
                )}
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  {item.pendencyCount > 0 ? (
                    <Badge tone="warning">
                      <AlertTriangle className="size-3" aria-hidden />
                      {item.pendencyCount}
                    </Badge>
                  ) : (
                    <Badge tone="success">em dia</Badge>
                  )}
                  {item.userCount > 0 && (
                    <span
                      className="flex items-center gap-1 text-xs text-slate-400"
                      title={`${item.userCount} usuário(s) com acesso`}
                    >
                      <Users className="size-3" aria-hidden />
                      {item.userCount}
                    </span>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Inclusão de fornecedor.
 *
 * O caminho normal é buscar no SA2 e escolher: assim código, loja, razão social
 * e CNPJ vêm do ERP, sem digitação. Quando o Protheus está fora — ou ainda não
 * foi configurado — a mesma janela permite cadastrar à mão, para o trabalho não
 * parar por causa da integração.
 */
function AddSupplierModal({
  open,
  onClose,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<ProtheusSupplierDto[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [protheusDown, setProtheusDown] = useState<string | null>(null);
  const [manual, setManual] = useState(false);
  const [form, setForm] = useState({ protheusCode: '', protheusStore: '', legalName: '' });
  const [competence, setCompetence] = useState(DEFAULT_COMPETENCE);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) return;
    // Reabrir a janela não pode trazer a busca anterior de volta.
    setTerm('');
    setResults(null);
    setProtheusDown(null);
    setManual(false);
    setForm({ protheusCode: '', protheusStore: '', legalName: '' });
    setCompetence(DEFAULT_COMPETENCE);
    setError(null);
    setFields({});
  }, [open]);

  async function handleSearch(event: FormEvent) {
    event.preventDefault();
    if (term.trim().length < 2) return;
    setSearching(true);
    setError(null);
    setProtheusDown(null);
    try {
      const response = await api.get<ProtheusSearchResponseDto>(
        `/protheus/suppliers?search=${encodeURIComponent(term.trim())}`,
      );
      setResults(response.items);
    } catch (err) {
      if (err instanceof ApiError && (err.status === 503 || err.status === 502)) {
        setProtheusDown(err.message);
        setManual(true);
      } else {
        setError(err instanceof ApiError ? err.message : 'Falha na busca.');
      }
    } finally {
      setSearching(false);
    }
  }

  async function enable(payload: Record<string, unknown>) {
    setSaving(true);
    setError(null);
    setFields({});
    try {
      await api.post('/monitoring', { ...payload, monitoredFrom: competence });
      onDone();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.fields ? null : err.message);
        setFields(err.fields ?? {});
      } else {
        setError('Não foi possível incluir o fornecedor.');
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title="Incluir fornecedor monitorado"
      description="A unidade é código + loja: cada loja tem ficha e nota próprias."
    >
      <div className="space-y-5">
        <Field
          label="Monitorar a partir da competência"
          name="competencia"
          placeholder="AAAA-MM"
          value={competence}
          onChange={(e) => setCompetence(e.target.value)}
          error={fields.monitoredFrom}
          hint="O portal não calcula nada antes desta competência."
        />

        {!manual && (
          <form onSubmit={handleSearch} className="space-y-3">
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Field
                  label="Buscar no Protheus"
                  name="protheus"
                  placeholder="Código, razão social ou CNPJ"
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                />
              </div>
              <Button type="submit" loading={searching} disabled={term.trim().length < 2}>
                <Search className="size-4" aria-hidden />
                Buscar
              </Button>
            </div>

            {results && results.length === 0 && (
              <p className="text-sm text-slate-500">Nada encontrado no SA2 para esse termo.</p>
            )}

            {results && results.length > 0 && (
              <ul className="max-h-64 space-y-1 overflow-y-auto">
                {results.map((item) => (
                  <li key={`${item.protheusCode}/${item.protheusStore}`}>
                    <button
                      type="button"
                      disabled={item.monitored || saving}
                      onClick={() =>
                        void enable({
                          protheusCode: item.protheusCode,
                          protheusStore: item.protheusStore,
                          legalName: item.legalName,
                          tradeName: item.tradeName ?? undefined,
                          cnpj: item.cnpj ?? undefined,
                          city: item.city ?? undefined,
                          state: item.state ?? undefined,
                        })
                      }
                      className={cn(
                        'flex w-full items-center gap-3 rounded-lg border px-3 py-2 text-left text-sm transition-colors',
                        item.monitored
                          ? 'cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400'
                          : 'border-slate-200 hover:border-brand-300 hover:bg-brand-50/50',
                      )}
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium">{item.legalName}</span>
                        <span className="block truncate text-xs text-slate-500">
                          {formatCnpj(item.cnpj)} · {formatPlace(item.city, item.state)}
                        </span>
                      </span>
                      <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs text-slate-600">
                        {item.protheusCode}/{item.protheusStore}
                      </span>
                      {item.monitored && <Badge tone="success">já monitorado</Badge>}
                      {item.blocked && !item.monitored && <Badge tone="danger">bloqueado</Badge>}
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <Button type="button" variant="link" size="sm" onClick={() => setManual(true)}>
              Cadastrar sem consultar o Protheus
            </Button>
          </form>
        )}

        {protheusDown && (
          <Alert variant="info">
            <span className="flex items-start gap-2">
              <ServerCog className="mt-0.5 size-4 shrink-0" aria-hidden />
              <span>{protheusDown} Até lá, cadastre pelos campos abaixo.</span>
            </span>
          </Alert>
        )}

        {manual && (
          <form
            className="space-y-3 border-t border-slate-200 pt-4"
            onSubmit={(event) => {
              event.preventDefault();
              void enable(form);
            }}
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <Field
                label="Código do fornecedor"
                name="protheusCode"
                placeholder="000043"
                value={form.protheusCode}
                onChange={(e) => setForm({ ...form, protheusCode: e.target.value })}
                error={fields.protheusCode}
              />
              <Field
                label="Loja"
                name="protheusStore"
                placeholder="0001"
                value={form.protheusStore}
                onChange={(e) => setForm({ ...form, protheusStore: e.target.value })}
                error={fields.protheusStore}
              />
            </div>
            <Field
              label="Razão social"
              name="legalName"
              value={form.legalName}
              onChange={(e) => setForm({ ...form, legalName: e.target.value })}
              error={fields.legalName}
              hint="O CNPJ e o endereço são preenchidos depois, pela ficha ou pelo Protheus."
            />
            <div className="flex justify-end gap-2">
              {!protheusDown && (
                <Button type="button" variant="ghost" onClick={() => setManual(false)}>
                  Voltar para a busca
                </Button>
              )}
              <Button
                type="submit"
                loading={saving}
                disabled={!form.protheusCode || !form.protheusStore}
              >
                <Building2 className="size-4" aria-hidden />
                Incluir
              </Button>
            </div>
          </form>
        )}

        {error && <Alert>{error}</Alert>}
      </div>
    </Modal>
  );
}
